import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { callAPI, callingAPI, leadAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CallRecordings } from '../components/CallRecordings';
import { formatDateTime } from '../lib/timeFormat';
import { CallingSystemHealth } from '../components/CallingSystemHealth';
import { Phone, PhoneCall, PhoneMissed, Clock, TrendingUp, CheckCircle2, Smartphone, Link2, X, Star, Plus, Trash2, Pencil, RefreshCw, ShieldAlert, WifiOff } from 'lucide-react';

const DEVICE_STATUS = {
  ACTIVE: { label: 'Active', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  OFFLINE: { label: 'Offline', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  STALE: { label: 'Stale', cls: 'bg-slate-200 text-slate-600', dot: 'bg-slate-400' },
  REVOKED: { label: 'Revoked', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

const RANGES = [['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'Week'], ['month', 'Month'], ['custom', 'Custom']];

const Stat = ({ label, value, sub, accent, testId }) => (
  <div className="clay-card p-4" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${accent || ''}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export const MyCallsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [range, setRange] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [queues, setQueues] = useState(null);
  const [devices, setDevices] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [linkCall, setLinkCall] = useState(null);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [pair, setPair] = useState(null); // { code, status }
  const isAdmin = user?.role === 'super_admin' || (user?.permissions || []).includes('telephony.device.manage');

  const load = useCallback(async () => {
    const params = range === 'custom' ? { date_from: from, date_to: to } : { period: range === 'yesterday' ? undefined : range };
    if (range === 'yesterday') {
      const d = new Date(); d.setDate(d.getDate() - 1);
      const iso = d.toISOString().slice(0, 10); params.date_from = iso; params.date_to = iso;
    }
    try {
      const [p, q] = await Promise.all([callAPI.performance(params), callAPI.queues()]);
      setData(p.data); setQueues(q.data);
    } catch (e) { /* silent */ }
  }, [range, from, to]);

  const loadOps = useCallback(async () => {
    try {
      const [d, u, c] = await Promise.all([
        callingAPI.myDevices(showRevoked).catch(() => ({ data: [] })),
        callingAPI.unmatched().catch(() => ({ data: { calls: [] } })),
        callingAPI.missedCallbacks().catch(() => ({ data: { calls: [] } }))
      ]);
      setDevices(d.data || []);
      setUnmatched(u.data?.calls || []);
      setCallbacks(c.data?.calls || []);
    } catch (e) { /* silent */ }
  }, [showRevoked]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOps(); }, [loadOps]);
  useEffect(() => {
    if (!pair || pair.status !== 'pending') return;
    const t = setInterval(async () => {
      try {
        const { data } = await callingAPI.pairStatus(pair.code);
        if (data.status !== 'pending') { setPair(p => ({ ...p, status: data.status })); if (data.status === 'completed') { toast.success('Device paired!'); loadOps(); } }
      } catch (e) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [pair, loadOps]);

  if (user && user.calling_controls?.my_calls_access === false) {
    return (
      <div className="clay-card p-8 text-center" data-testid="mycalls-disabled">
        <PhoneCall className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
        <h2 className="text-lg font-medium">My Calls is disabled</h2>
        <p className="text-sm text-muted-foreground mt-1">Access to your call analytics has been turned off by an administrator.</p>
        <button onClick={() => navigate('/dashboard')} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm">Back to Dashboard</button>
      </div>
    );
  }

  const setDefault = async (id) => {
    try { await callingAPI.setDefaultDevice(id); toast.success('Default device updated'); loadOps(); }
    catch (e) { toast.error('Failed to set default device'); }
  };

  const renameDevice = async (d) => {
    const name = window.prompt('Rename device', d.device_name || d.device_model || 'Android Device');
    if (!name || !name.trim()) return;
    try { await callingAPI.renameDevice(d.id, name.trim()); toast.success('Device renamed'); loadOps(); }
    catch (e) { toast.error('Rename failed'); }
  };

  const removeDevice = async (d) => {
    if (!window.confirm(`Remove "${d.device_name || d.device_model}"? It will be revoked and can no longer receive Click-to-Call. Call history is kept.`)) return;
    try { await callingAPI.removeDevice(d.id); toast.success('Device removed (revoked)'); loadOps(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  const deleteDevice = async (d) => {
    if (!window.confirm('Permanently delete this revoked device? Call history is preserved.')) return;
    try { await callingAPI.deleteDevicePermanent(d.id); toast.success('Device deleted'); loadOps(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete not allowed'); }
  };

  const startPairing = async (recoverDevice = null) => {
    try {
      // Reconnect always names the exact row being restored - a reinstalled app rotates its new
      // installation identity onto that same registration instead of creating a second one.
      const recoverId = recoverDevice?.recovery_eligible === false ? null : recoverDevice?.id || null;
      const { data } = await callingAPI.pairStart(recoverId ? { recover_device_id: recoverId } : undefined);
      setPair({ code: data.pairing_code, status: 'pending', recovering: data.recover_device_masked || null });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start pairing'); }
  };

  const searchLeads = async (term) => {
    setLeadSearch(term);
    if (!term || term.length < 2) { setLeadResults([]); return; }
    try { const { data } = await leadAPI.getAll({ search: term, limit: 10 }); setLeadResults(Array.isArray(data) ? data : (data.items || data.leads || [])); }
    catch (e) { setLeadResults([]); }
  };

  const doLink = async (leadId) => {
    try {
      await callingAPI.linkLead(linkCall.id, leadId);
      toast.success('Call linked to lead');
      setLinkCall(null); setLeadSearch(''); setLeadResults([]);
      loadOps();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link call'); }
  };

  const markCallbackDone = async (id) => {
    try { await callingAPI.callbackDone(id); toast.success('Callback marked done'); loadOps(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="my-calls-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10"><PhoneCall className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-outfit font-semibold">My Calls</h1>
          <p className="text-muted-foreground">Your calling activity and performance.</p>
        </div>
      </div>

      <CallingSystemHealth />

      <CallRecordings />

      <div className="flex flex-wrap gap-2">
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`mycalls-range-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm ${range === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{l}</button>
        ))}
        {range === 'custom' && (
          <span className="flex gap-2 items-center">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="mycalls-from" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
            <input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="mycalls-to" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
          </span>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Calls Attempted" value={data.attempted} testId="mycalls-attempted" />
          <Stat label="Connected" value={data.connected} accent="text-green-600" testId="mycalls-connected" />
          <Stat label="Not Connected" value={data.not_connected} accent="text-amber-600" testId="mycalls-notconnected" />
          <Stat label="Connection Rate" value={`${data.connection_rate}%`} testId="mycalls-rate" />
          {data.call_target && (
            data.call_target.target_set ? (
              <Stat label="Call Target"
                value={`${data.call_target.achieved} / ${data.call_target.target_value}`}
                accent={data.call_target.achievement_pct >= 100 ? 'text-green-600' : 'text-primary'}
                testId="mycalls-target" />
            ) : (
              <div className="clay-card p-4" data-testid="mycalls-target-notset">
                <p className="text-xs text-muted-foreground">Call Target</p>
                <p className="text-lg font-outfit font-semibold text-slate-400">Target Not Set</p>
              </div>
            )
          )}
          <Stat label="Total Talk Time" value={data.total_talk_hms} testId="mycalls-talk" />
          <Stat label="Avg Talk Time" value={data.avg_talk_hms} testId="mycalls-avgtalk" />
          <Stat label="Avg Ring Time" value={`${data.avg_ring_seconds}s`} testId="mycalls-avgring" />
          <Stat label="Follow-ups Created" value={data.follow_ups_created} testId="mycalls-fu" />
          <Stat label="Hot Leads" value={data.hot_leads_identified} accent="text-red-600" testId="mycalls-hot" />
          <Stat label="Quotations" value={data.quotations_triggered} testId="mycalls-quotes" />
          <Stat label="Leads Touched" value={data.leads_touched} testId="mycalls-touched" />
        </div>
      )}

      {/* My Calling Devices */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-outfit font-medium flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> My Calling Devices</h3>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground flex items-center gap-1 cursor-pointer" data-testid="toggle-revoked">
              <input type="checkbox" checked={showRevoked} onChange={e => setShowRevoked(e.target.checked)} /> Show revoked
            </label>
            <button onClick={startPairing} data-testid="add-device-btn" className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs flex items-center gap-1 hover:opacity-90 transition-opacity">
              <Plus className="h-3.5 w-3.5" /> Add Calling Device
            </button>
          </div>
        </div>
        {devices.length === 0 ? (
          <div className="clay-card p-4 text-sm text-muted-foreground" data-testid="no-devices">
            No registered device. Tap <b>Add Calling Device</b> to pair your phone with the GNONS Calling app.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {devices.map(d => {
              const st = DEVICE_STATUS[d.status] || DEVICE_STATUS.STALE;
              return (
              <div key={d.id} className={`clay-card p-4 ${d.status === 'REVOKED' ? 'opacity-60' : ''}`} data-testid={`device-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{d.device_name || d.device_model || 'Android Device'}</div>
                  {d.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1 shrink-0" data-testid={`default-badge-${d.id}`}><Star className="h-3 w-3" /> Default</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{[d.manufacturer, d.device_model].filter(Boolean).join(' · ') || 'Android'} · {d.app_version || 'v?'}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${st.cls}`} data-testid={`device-status-${d.id}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {st.label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${d.fcm_status === 'Ready' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'}`} title="FCM instant-wake status">FCM: {d.fcm_status}</span>
                </div>
                {d.last_seen_at && <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><WifiOff className="h-3 w-3" /> Last seen: {formatDateTime(d.last_seen_at)}</div>}
                {d.last_sync && <div className="text-xs text-muted-foreground">Last sync: {formatDateTime(d.last_sync)}</div>}
                <div className="text-[10px] text-muted-foreground/70 mt-1">ID …{d.device_id_short}</div>
                <div className="flex items-center gap-3 mt-3 flex-wrap border-t border-slate-100 dark:border-slate-800 pt-2">
                  {!d.is_default && d.status !== 'REVOKED' && d.eligible_for_calls && (
                    <button onClick={() => setDefault(d.id)} data-testid={`set-default-${d.id}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Star className="h-3 w-3" /> Set default</button>
                  )}
                  {d.status !== 'REVOKED' && <button onClick={() => renameDevice(d)} data-testid={`rename-${d.id}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><Pencil className="h-3 w-3" /> Rename</button>}
                  {d.status !== 'REVOKED' && <button onClick={() => startPairing(d)} data-testid={`reconnect-${d.id}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Reconnect</button>}
                  {d.status !== 'REVOKED' && <button onClick={() => removeDevice(d)} data-testid={`remove-${d.id}`} className="text-xs text-red-600 hover:underline flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>}
                  {isAdmin && d.status === 'REVOKED' && !d.is_default && <button onClick={() => deleteDevice(d)} data-testid={`delete-${d.id}`} className="text-xs text-red-700 hover:underline flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Delete permanently</button>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {pair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pair-modal" onClick={() => setPair(null)}>
          <div className="clay-card p-6 max-w-sm w-full bg-background" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Pair Calling Device</h4>
              <button onClick={() => setPair(null)} data-testid="pair-close"><X className="h-4 w-4" /></button>
            </div>
            {pair.status === 'pending' && <>
              <p className="text-sm text-muted-foreground">In the GNONS Calling App, open <b>Register / Pair Device</b> and enter this code (valid 10 min):</p>
              {pair.recovering && (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300" data-testid="pair-recovery-target">
                  This code will restore the existing registration <b>{pair.recovering}</b> — no new device will be created.
                </p>
              )}
              <div className="text-3xl font-mono font-bold tracking-widest text-center my-4 py-3 bg-slate-100 dark:bg-slate-800 rounded-lg" data-testid="pair-code">{pair.code}</div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Waiting for the app to confirm…</p>
            </>}
            {pair.status === 'completed' && <p className="text-sm text-green-600 flex items-center gap-2" data-testid="pair-success"><CheckCircle2 className="h-5 w-5" /> Device paired successfully!</p>}
            {pair.status === 'expired' && <p className="text-sm text-red-600" data-testid="pair-expired">Code expired. Please try again.</p>}
          </div>
        </div>
      )}

      {/* Unmatched Calls */}
      {unmatched.length > 0 && (
        <div>
          <h3 className="text-lg font-outfit font-medium mb-3 flex items-center gap-2"><Link2 className="h-5 w-5 text-amber-600" /> Unmatched Calls ({unmatched.length})</h3>
          <div className="clay-card divide-y divide-slate-100 dark:divide-slate-800" data-testid="unmatched-list">
            {unmatched.slice(0, 25).map(c => (
              <div key={c.id} className="p-3 flex items-center justify-between gap-3" data-testid={`unmatched-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.phone_number || 'Unknown number'}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.direction || 'call'} · {c.call_result || '—'}{c.employee ? ` · ${c.employee}` : ''} · {c.created_at ? formatDateTime(c.created_at) : ''}
                  </div>
                </div>
                <button onClick={() => { setLinkCall(c); setLeadSearch(''); setLeadResults([]); }} data-testid={`link-${c.id}`}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-1">
                  <Link2 className="h-3 w-3" /> Link to Lead
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missed Callbacks */}
      {callbacks.length > 0 && (
        <div>
          <h3 className="text-lg font-outfit font-medium mb-3 flex items-center gap-2"><PhoneMissed className="h-5 w-5 text-red-600" /> Missed — Needs Callback ({callbacks.length})</h3>
          <div className="clay-card divide-y divide-slate-100 dark:divide-slate-800" data-testid="callbacks-list">
            {callbacks.slice(0, 25).map(c => (
              <div key={c.id} className="p-3 flex items-center justify-between gap-3" data-testid={`callback-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.phone_number || 'Unknown number'}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.call_result} · {c.created_at ? formatDateTime(c.created_at) : ''}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.lead_id && <button onClick={() => navigate(`/leads/${c.lead_id}`)} className="text-xs text-primary hover:underline">Open Lead</button>}
                  <button onClick={() => markCallbackDone(c.id)} data-testid={`callback-done-${c.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200">Done</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {queues && (
        <div>
          <h3 className="text-lg font-outfit font-medium mb-3">My Call Queues</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              ['new_leads_not_called', 'New — Not Called', PhoneMissed, 'text-red-600'],
              ['hot_leads_not_called_today', 'Hot — Not Called Today', TrendingUp, 'text-red-600'],
              ['follow_up_call_overdue', 'Follow-up Call Overdue', Clock, 'text-amber-600'],
              ['no_answer_retry_due', 'No Answer — Retry Due', Phone, 'text-amber-600'],
              ['connected_no_disposition', 'Connected — No Disposition', CheckCircle2, 'text-blue-600'],
              ['high_budget_waiting_for_call', 'High-Budget Waiting', TrendingUp, 'text-purple-600'],
            ].map(([key, label, Icon, accent]) => (
              <div key={key} className="clay-card p-4" data-testid={`queue-${key}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm">{label}</div>
                  <Icon className={`h-4 w-4 ${accent}`} />
                </div>
                <div className={`text-2xl font-semibold mt-1 ${accent}`}>{queues[key]?.count ?? 0}</div>
                {(queues[key]?.items || []).slice(0, 2).map((it, i) => (
                  it.lead_id ? <button key={i} onClick={() => navigate(`/leads/${it.lead_id}`)} className="block text-left text-xs text-primary hover:underline truncate mt-1">{it.client || it.lead_id}</button> : null
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link-to-Lead modal */}
      {linkCall && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setLinkCall(null)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="link-lead-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">Link Call to Lead</h3>
              <button onClick={() => setLinkCall(null)}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{linkCall.phone_number} · {linkCall.created_at ? formatDateTime(linkCall.created_at) : ''}</p>
            <input autoFocus value={leadSearch} onChange={e => searchLeads(e.target.value)} data-testid="link-lead-search"
              placeholder="Search lead by name / mobile…" className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900" />
            <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {leadResults.map(l => (
                <button key={l.id} onClick={() => doLink(l.id)} data-testid={`link-lead-option-${l.id}`}
                  className="w-full text-left p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                  <div className="text-sm font-medium">{l.customer_name || l.company_name || 'Lead'}</div>
                  <div className="text-xs text-muted-foreground">{l.primary_mobile || l.mobile || ''} · {l.status || ''}</div>
                </button>
              ))}
              {leadSearch.length >= 2 && leadResults.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground text-center">No matching leads. Do not create fake leads — leave unmatched if none.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default MyCallsPage;
